<script setup lang="ts">
import { tpgIconClose, tpgIconKeyboardArrowLeft } from '@tpg/grammar-web-icons';
import { onMounted, toRefs } from 'vue';

import {
  GrammarLoader,
  GrammarOverlayCard,
  GrammarQuaternaryButton,
  GrammarText,
} from '@nora-monorepo/grammar-ui';

import EditingToolbarView from '#nora-ui/map/components/drawtools/editing/EditingToolbarView.vue';
import SymbolPackView from '#nora-ui/map/components/drawtools/symbol-tool/SymbolPackView.vue';
import type { SymbolTool } from '#nora-ui/map/drawtools/symbol-tool/symbol-tool';
import { useMapStore } from '#nora-ui/map/stores/map-store';

interface Props {
  mapId: string;
  tool: SymbolTool;
}

const props = defineProps<Props>();

const mapStore = useMapStore();

const { tool, mapId } = toRefs(props);

const { symbolPacks, selectedSymbol } = toRefs(tool.value.symbolToolStore);

const map = mapStore.getMapByIdOrThrow(mapId.value);

function onBackButtonPressed() {
  map.drawToolUI.openToolList();
}

onMounted(async () => {
  await tool.value.symbolToolStore.loadSymbolPacks();
});
</script>

<template>
  <GrammarOverlayCard
    v-if="!selectedSymbol"
    label="Symbols"
    class="w-75 h-75 flex flex-col"
  >
    <template #header-leading-buttons>
      <GrammarQuaternaryButton
        :icon-template="tpgIconKeyboardArrowLeft"
        @click="onBackButtonPressed"
      />
    </template>

    <template #header-trailing-buttons>
      <GrammarQuaternaryButton
        :icon-template="tpgIconClose"
        @click="() => tool.onCloseButtonPressed()"
      />
    </template>

    <div
      v-if="symbolPacks == null"
      class="flex-1 flex items-center justify-center"
    >
      <GrammarLoader />
    </div>
    <div
      v-else-if="!symbolPacks.length"
      class="flex-1 flex items-center justify-center p-4 text-center"
    >
      <GrammarText
        text="No symbol packs available."
        size="medium"
      />
    </div>
    <div
      v-else-if="symbolPacks.length"
      class="px-2 overflow-y-auto flex-1 min-h-0 max-h-57.5 flex flex-col gap-2"
    >
      <SymbolPackView
        v-for="pack in symbolPacks"
        :key="pack.id"
        :symbol-pack="pack"
        :map-id="mapId"
        :tool="tool"
      />
    </div>
  </GrammarOverlayCard>
  <EditingToolbarView
    v-else
    :draw-tool="tool"
    :map-id="mapId"
  />
</template>
